import type { ConsentPreferencesT } from "@/app/types/consent";

type Category = "functional" | "analytics" | "marketing";

// Registry of first-party cookies to delete when a category is revoked.
// Extend this list with your own cookie names or regex patterns per category.
const COOKIE_REGISTRY: Record<Category, (string | RegExp)[]> = {
  functional: [
    /^_sentry/, // Sentry cookies
  ],
  analytics: [
    /^_ga.*/, // Google Analytics (alle Varianten)
    /^_gid.*/, // Google Analytics Session
    /^_gat.*/, // Google Analytics Request
    /^_gcl_/, // Google Analytics Click
    /^_gtm/, // Google Tag Manager
    /^_ga_E2SBTDQK0Q/,
  ],
  marketing: [
    /^_fbp/, // Facebook Pixel
    /^_fbc/, // Facebook Pixel
    /^datr$/, // Facebook login state
    /^fr$/, // Facebook referrer
    /^ps_l$/, // Facebook page session
    /^ps_n$/, // Facebook page session
    /^sb$/, // Facebook session
  ],
};

function getAllCookies(): { name: string; value: string }[] {
  if (typeof document === "undefined") return [];
  return document.cookie
    .split("; ")
    .filter(Boolean)
    .map((pair) => {
      const [name, ...rest] = pair.split("=");
      return { name, value: rest.join("=") };
    });
}

function matches(name: string, matcher: string | RegExp): boolean {
  return typeof matcher === "string" ? name === matcher : matcher.test(name);
}

function deleteCookieEverywhere(name: string) {
  if (typeof location === "undefined" || !location.hostname) return;
  const baseOptions = `path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  // Current host
  document.cookie = `${name}=; ${baseOptions}`;
  // Try dot-domain variants (best-effort; may not cover all cases)
  try {
    const parts = location.hostname.split(".");
    for (let i = 0; i < parts.length - 1; i++) {
      const domain = "." + parts.slice(i).join(".");
      document.cookie = `${name}=; domain=${domain}; ${baseOptions}`;
    }
  } catch {
    // SecurityError catch - access to location.hostname is blocked
    // Only delete cookie for current host (already done above)
  }
}

export function runConsentRevocationCleanup(
  previous: ConsentPreferencesT,
  next: ConsentPreferencesT
) {
  const categories: Category[] = ["functional", "analytics", "marketing"];
  const revoked = categories.filter((c) => previous[c] && !next[c]);
  if (revoked.length === 0) return;

  const cookies = getAllCookies();
  for (const category of revoked) {
    const matchers = COOKIE_REGISTRY[category] || [];
    for (const { name } of cookies) {
      if (matchers.some((m) => matches(name, m))) {
        deleteCookieEverywhere(name);
      }
    }
  }
}
